import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import logo from '../../assets/logo.png';
import { useAuth } from '../../context/AuthContext';
import api from '../../api';
import axios from 'axios';
import debounce from 'lodash.debounce';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faTachometerAlt } from '@fortawesome/free-solid-svg-icons';
import defaultProfilePic from '../../assets/defaultProfilePic.png';
import { getProfilePictureUrl } from '../../utils/apiHelper';
import './InstaTopbar.css';
import useBlockCheck from '../../hooks/useBlockCheck';
import Translate from '../../components/Translate/Translate';
import { FaGlobe } from 'react-icons/fa';

function InstaTopbar() {
  useBlockCheck('instructor');
  const { logout, profile, profileLoading, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [userName, setUserName] = useState('Learner');
  const [profilePicture, setProfilePicture] = useState();
  const [showDropdown, setShowDropdown] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const dropdownRef = useRef(null);
  const translateRef = useRef(null);
  const globeIconRef = useRef(null);

  // ✅ Fetch Profile Data
  useEffect(() => {
    if (!profileLoading && profile) {
      const name = profile.name || 'Learner';
      setUserName(name);
      setProfilePicture(
        getProfilePictureUrl(profile.profilePicture, api.img, defaultProfilePic, name),
      );
    }
  }, [profile, profileLoading]);

  // ✅ Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Function to position language menu (for tablet/mobile)
  const positionLanguageMenu = useCallback(() => {
    if (globeIconRef.current) {
      const rect = globeIconRef.current.getBoundingClientRect();
      const menuElement = document.querySelector('body > .translate-mobile-menu') ||
                         document.querySelector('.translate-mobile-menu');
      if (menuElement) {
        const isMobile = window.innerWidth <= 1000;
        
        if (isMobile) {
          // Mobile/Tablet: position DOWNWARDS, starting just below the globe icon
          menuElement.style.top = `${rect.bottom + 8}px`;
          menuElement.style.bottom = 'auto';
          menuElement.style.transform = 'none';
          menuElement.style.left = `${rect.left}px`;
          menuElement.style.right = 'auto';
        } else {
          // Desktop: position downwards
          menuElement.style.top = `${rect.bottom + 10}px`;
          menuElement.style.bottom = 'auto';
          menuElement.style.transform = 'none';
          menuElement.style.right = `${Math.max(10, window.innerWidth - rect.right)}px`;
          menuElement.style.left = 'auto';
        }
      }
    }
  }, []);

  // Watch for menu opening and position it correctly
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const menuElement = document.querySelector('body > .translate-mobile-menu');
      if (menuElement) {
        positionLanguageMenu();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [positionLanguageMenu]);

  // Update menu position on window resize
  useEffect(() => {
    const handleResize = () => {
      const menuElement = document.querySelector('body > .translate-mobile-menu');
      if (menuElement) {
        positionLanguageMenu();
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [positionLanguageMenu]);

  // ✅ Fetch Notifications Count
  useEffect(() => {
    const fetchNotificationCount = async () => {
      try {
        const response = await axios.get(api.instructorNotifications, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // If your API has an 'isRead' property, use the filter below:
        const unreadCount = response.data.notifications?.filter(notif => !notif.isRead).length || 0;
        // If not, just count all notifications:
        // const unreadCount = response.data.notifications.length;
        setNotificationCount(unreadCount);
      } catch (err) {
        console.error('Failed to fetch notifications:', err);
      }
    };

    fetchNotificationCount();
  }, [token]);

  // ✅ Search Logic (Optional)
  const fetchSearchResults = async (query) => {
    try {
      const response = await axios.get(`${api.getAllCourses}?search=${query}`);
      setSearchResults(response.data.courses || []);
    } catch (error) {
      console.error('Error fetching search results:', error);
    }
  };

  const debouncedFetchResults = useCallback(
    debounce((query) => {
      if (query.trim()) {
        fetchSearchResults(query);
      } else {
        setSearchResults([]);
      }
    }, 300),
    [],
  );

  useEffect(() => {
    debouncedFetchResults(searchQuery);
    return () => debouncedFetchResults.cancel();
  }, [searchQuery, debouncedFetchResults]);

  // ✅ Navigation Handlers
  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleNotifications = () => {
    navigate('/Instructor-notifications');
  };

  return (
    <div className="insta-home-nav">
      <header className="insta-homenavbar">
        {/* Logo */}
        <Link to="/dashboard">
          <img
            src={logo}
            alt="UlsaApp Logo"
            className="insta-homelogo-img"
          />
        </Link>

        {/* Language selector - hidden but functional for globe icon */}
        <div className="insta-translate-hidden">
          <Translate ref={translateRef} />
        </div>

        {/* Language/Globe icon - for tablet and mobile */}
        <div
          ref={globeIconRef}
          className="insta-globe-icon-wrapper"
          onClick={() => {
            if (translateRef.current) {
              translateRef.current.openMenu();
              setTimeout(() => {
                positionLanguageMenu();
              }, 10);
              setTimeout(() => {
                positionLanguageMenu();
              }, 50);
              setTimeout(() => {
                positionLanguageMenu();
              }, 100);
            }
          }}
        >
          <FaGlobe
            className="insta-globe-icon"
            title="Select Language"
          />
        </div>

        {/* Language selector - visible for desktop/web view */}
        <div className="insta-translate-desktop">
          <Translate />
        </div>

        {/* 🔔 Notifications */}
        {/* <div className="insta-notification" onClick={handleNotifications}>
          <FontAwesomeIcon icon={faBell} className="insta-bell-icon" />
          {notificationCount > 0 && (
            <span className="insta-notification-badge">
              {notificationCount}
            </span>
          )}
        </div> */}

        {/* Back to Dashboard */}
        {/* {location.pathname !== "/dashboard" && (
          <button className="back-to-dashboard-btn" onClick={() => navigate("/dashboard")}>
            <FontAwesomeIcon icon={faTachometerAlt} className="back-icon" /> Dashboard
          </button>
        )} */}

        {/* 🔎 Optional Search (Uncomment if you want to use) */}
        {/* <form className="insta-homesearch-bar">
          <input
            type="text"
            placeholder="Search for courses, topics, or resources..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchResults.length > 0 && (
            <div className="insta-search-suggestions">
              {searchResults.map((course) => (
                <div
                  key={course._id}
                  className="insta-search-suggestion-item"
                  onClick={() => navigate(`/courses/${course._id}`)}
                >
                  <h4>{course.title}</h4>
                  <p>{course.description}</p>
                </div>
              ))}
            </div>
          )}
        </form> */}

        {/* 👤 Profile Dropdown */}
        <div
          className="insta-profile-icon-container"
          title="Profile"
          ref={dropdownRef}
        >
          <img
            src={profilePicture}
            alt="profile"
            className="insta-profile-icon"
            onClick={() => setShowDropdown(!showDropdown)}
          />
          {showDropdown && (
            <div className="insta-dropdown-menu">
              <div className="insta-profile-info">
                <img src={profilePicture} alt="profile" className="insta-dropdown-profile-icon" />
                <p className="insta-dropdown-item">{userName}</p>
              </div>
              <button className="insta-menu-item" onClick={handleNotifications}>Notifications</button>
              <button className="insta-menu-item" onClick={() => navigate('/edit-profile')}>Edit Profile</button>
              {/* <button className="insta-menu-item" onClick={() => navigate("/message")}>Messages</button> */}
              <button className="insta-menu-item" onClick={() => navigate('/account-settings')}>Account Settings</button>
              <button className="insta-menu-item" onClick={() => navigate('/instructor-payout')}>Payment</button>
              <button className="insta-menu-item" onClick={() => navigate('/performance')}>Performance</button>
              <button className="insta-menu-item" onClick={() => navigate('/instructor-feedback')}>Feedback</button>
              <button className="insta-logout-button" onClick={handleLogout}>Logout</button>
            </div>
          )}
        </div>
      </header>
    </div>
  );
}

export default InstaTopbar;
